import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import { Trophy, MapPin, Calendar, Check, X } from 'lucide-react'
import type { PublicFormula1PredictionItem } from '../services/publicService'
import type { Formula1Race } from '../types/formula1'

/** Points per position (same as backend). */
const POINTS_MAP: Record<number, number> = {
  1: 25,
  2: 18,
  3: 15,
  4: 12,
  5: 10,
  6: 8,
  7: 6,
  8: 4,
  9: 2,
  10: 1,
}

const positionMedal = (pos: number) => {
  if (pos === 1) return '🥇'
  if (pos === 2) return '🥈'
  if (pos === 3) return '🥉'
  return null
}

interface PublicFormula1RaceResultModalProps {
  isOpen: boolean
  onClose: () => void
  prediction: PublicFormula1PredictionItem
  race: Formula1Race
}

const PublicFormula1RaceResultModal: React.FC<PublicFormula1RaceResultModalProps> = ({
  isOpen,
  onClose,
  prediction,
  race,
}) => {
  const { t } = useTranslation()

  if (!isOpen) return null

  const predictedPositions = prediction.predictedPositions ?? {}
  const actualResults = race.actualResults ?? {}

  const predictedByPos: Record<number, string> = {}
  Object.entries(predictedPositions).forEach(([driver, pos]) => {
    const p = Number(pos)
    if (p >= 1 && p <= 10) predictedByPos[p] = driver
  })

  const actualByPos: Record<number, string> = {}
  Object.entries(actualResults).forEach(([driver, pos]) => {
    const p = Number(pos)
    if (p >= 1 && p <= 10) actualByPos[p] = driver
  })

  const totalPoints = prediction.totalPoints ?? 0
  const raceDateStr = race.raceDate
    ? new Date(race.raceDate).toLocaleDateString(undefined, { dateStyle: 'medium' })
    : null
  const hasActual = actualResults && Object.keys(actualResults).length > 0

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const modalContent = (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-2xl glass-strong rounded-2xl p-4 sm:p-6 max-h-[90vh] overflow-hidden flex flex-col shadow-xl border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with icon and race info */}
        <div className="flex gap-3 mb-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center shadow-sm">
            <Trophy className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-foreground">
              Race {race.day}: {race.city}
              {race.country ? `, ${race.country}` : ''}
            </h2>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
              {raceDateStr && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {raceDateStr}
                </span>
              )}
              {race.city && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {race.city}{race.country ? `, ${race.country}` : ''}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {hasActual ? (
          <>
            {/* Total points badge - green for played */}
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500/20 text-emerald-400 font-bold text-lg">
                <Trophy className="w-5 h-5" />
                +{formatPoints(totalPoints, t)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mb-2 font-medium">
              {t('formula1.predictionsByPlace') ?? 'Your prediction vs actual results by position'}
            </p>
            {/* Table header */}
            <div className="grid grid-cols-[2.5rem_1fr_1fr_3rem] sm:grid-cols-[3rem_1.2fr_1.2fr_3.5rem] gap-2 sm:gap-4 items-center px-3 py-2 rounded-lg bg-muted/20 border border-muted/30 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              <span className="text-center">#</span>
              <span>{t('publicFormula1.yourPrediction') ?? 'Your prediction'}</span>
              <span>{t('formula1.actualResults') ?? 'Actual'}</span>
              <span className="text-right">{t('roomPage.pts') ?? 'Pts'}</span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-2 pr-1 pb-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((pos) => {
                const predictedDriver = predictedByPos[pos] ?? '—'
                const actualDriver = actualByPos[pos] ?? '—'
                const isCorrect = predictedDriver !== '—' && actualDriver !== '—' && predictedDriver === actualDriver
                const pts = isCorrect ? (POINTS_MAP[pos] ?? 0) : 0
                const medal = positionMedal(pos)
                return (
                  <div
                    key={pos}
                    className="flex items-center gap-3 rounded-xl border border-muted/30 bg-muted/5 px-3 py-2.5 sm:px-4 sm:py-3"
                  >
                    <div className="flex-shrink-0 w-10 sm:w-12 flex items-center justify-center gap-1">
                      {medal ? (
                        <span className="text-lg" title={`P${pos}`}>{medal}</span>
                      ) : (
                        <span className="text-sm font-bold text-muted-foreground">P{pos}</span>
                      )}
                    </div>
                    <div className="flex-1 grid grid-cols-[1fr_1fr_auto] sm:grid-cols-[1.2fr_1.2fr_3rem] gap-2 sm:gap-4 items-center min-w-0">
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide hidden sm:block">
                          {t('publicFormula1.yourPrediction') ?? 'Your prediction'}
                        </p>
                        <p className="text-sm font-medium text-foreground truncate" title={predictedDriver}>
                          {predictedDriver}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide hidden sm:block">
                          {t('formula1.actualResults') ?? 'Actual'}
                        </p>
                        <p className="text-sm font-medium text-foreground truncate" title={actualDriver}>
                          {actualDriver}
                        </p>
                      </div>
                      <div className="flex items-center justify-end gap-1">
                        {isCorrect ? (
                          <span className="inline-flex items-center gap-0.5 text-emerald-400 font-semibold text-sm">
                            <Check className="w-4 h-4" />
                            +{pts}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 text-muted-foreground text-sm">
                            <X className="w-3.5 h-3.5" />
                            0
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-amber-500/90 mb-2 font-medium">
              {t('publicFormula1.yourPredictionsOnly') ?? 'Your predictions (only visible to you)'}
            </p>
            <p className="text-xs text-muted-foreground mb-3">
              {t('publicFormula1.resultsAfterRace') ?? 'Results will appear here after the race.'}
            </p>
            <div className="grid grid-cols-[2.5rem_1fr] sm:grid-cols-[3rem_1fr] gap-2 items-center px-3 py-2 rounded-lg bg-muted/20 border border-muted/30 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              <span className="text-center">#</span>
              <span>{t('publicFormula1.yourPrediction') ?? 'Your prediction'}</span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden space-y-2 pr-1 pb-4">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((pos) => {
                const predictedDriver = predictedByPos[pos] ?? '—'
                const medal = positionMedal(pos)
                return (
                  <div
                    key={pos}
                    className="flex items-center gap-3 rounded-xl border border-muted/30 bg-muted/5 px-3 py-2.5 sm:px-4 sm:py-3"
                  >
                    <div className="flex-shrink-0 w-10 sm:w-12 flex items-center justify-center gap-1">
                      {medal ? (
                        <span className="text-lg" title={`P${pos}`}>{medal}</span>
                      ) : (
                        <span className="text-sm font-bold text-muted-foreground">P{pos}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate" title={predictedDriver}>
                        {predictedDriver}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default PublicFormula1RaceResultModal
